/**
 * InvoicesPage — Tela de cobrança (domínio billing): 1 linha = 1 invoice.
 *
 * Host: billing REST API (GET /billing/invoices).
 * Ações por linha: detalhes (modal) e boleto (modal: download/cancelar).
 *
 * Contrato e Natureza vêm do TÍTULO (settlement_v2.title_debt), não da fatura — o backend resolve
 * por (operação, charge) com EXISTS correlacionado, sem view nova. Ver `charge_titles` no
 * repositório do billing e o porquê de não ser JOIN.
 *
 * TODO: juros/multa/protesto quando entrarem no boleto.
 */
import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageList from '../../templates/PageList';
import BadgeStatus from '../../atoms/BadgeStatus';
import PartyCell from '../../molecules/PartyCell';
import appConfig from '../../../config/app-config';
import { FilterBuilder } from '../../molecules/FilterBuilder';
import { AppliedFilter, FilterSchema, Operator } from '../../molecules/FilterBuilder/types';
import {
    invoiceFilterSchema, serializeFilters, BUSINESS_REFERENCE_LABELS,
} from './constants/invoiceFilterSchema';
import InvoiceDetailsModal from './modals/InvoiceDetailsModal';
import BoletoModal from './modals/BoletoModal';
import InvoiceBillingEventsModal from './modals/InvoiceBillingEventsModal';
import CommentCell from '../../molecules/CommentCell';
import AddInvoiceCommentModal from './modals/AddInvoiceCommentModal';
import { invoiceAmountOf, nominalAmountOf } from './services/invoiceAmounts';
import { formatDateBR } from '../../../utils/DateUtils';
import { FaInfoCircle, FaBarcode, FaGavel, FaCommentDots } from 'react-icons/fa';
import { FaHandHoldingDollar, FaScaleBalanced } from 'react-icons/fa6';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo'
    | 'pink' | 'olive' | 'teal' | 'gold' | 'todo' | 'turquoise';

// Mesma paleta da tela de Títulos: cobrança em aberto é gold. Antes era yellow, que nesta tela
// competia com o vermelho do vencido e não casava com o selo do mesmo título na outra tela.
const statusColors: Record<string, BadgeColor> = {
    OPEN: 'gold',
    PAID: 'green',
    CANCELLED: 'red',
    // não é gravado hoje (pagamento parcial deriva outra fatura), mas dado legado pode ter
    PARTIALLY_PAID: 'olive',
};

const scenarioColors: Record<string, BadgeColor> = {
    RECEIVABLE: 'indigo',
    INSTALLMENT: 'blue',
    GUARANTEE: 'gray',
};

const isOverdue = (row: any) =>
    row.status === 'OPEN' && row.dueDate && row.dueDate < new Date().toISOString().slice(0, 10);

// o ATable substitui a classe do cabeçalho inteira quando cssClass é informado, então a borda
// e a largura precisam vir junto — daí a constante em vez de só 'text-right'
/**
 * Coluna OCULTA, não removida: o `#` (charge) e a `Fatura` são UUIDs encurtados que não dizem nada
 * a quem opera, mas são o que o desenvolvedor usa para achar a linha no banco. Ficam no código, com
 * `display:none` no cabeçalho e na célula — descomentar a constante devolve as duas.
 */
const OCULTA = 'hidden';

const DIREITA = 'border-b text-right w-fit';

/* Cabeçalho que NÃO pode quebrar em várias linhas.
 *
 * ⚠️ O `width: '1%'` encolhe a coluna até o conteúdo — e enquanto a tabela CARREGA ou está VAZIA
 * não há conteúdo nenhum, então ela encolhe até a maior PALAVRA do título: "Emitente / Cedente"
 * virava três linhas. Com `whitespace-nowrap` o piso passa a ser o título inteiro, que é o mínimo
 * correto para uma coluna.
 *
 * ⚠️ `cssClass` SUBSTITUI o default do ATable (`border-b text-center w-fit`), não soma — por isso
 * as três classes de base vêm junto. Mesmo motivo do DIREITA logo acima. */
const NOWRAP = 'border-b text-center w-fit whitespace-nowrap';

/**
 * A cor do valor da fatura: laranja o que ainda se espera, verde o que entrou.
 *
 * ⚠️ Só ABERTA e PAGA ganham cor. Cancelada e renegociada ficam neutras de propósito: laranja ali
 * pediria uma cobrança que não existe mais, e é justamente nesses estados que alguém varre a lista
 * procurando o que ainda dá para receber.
 *
 * ⚠️ Combina com o que a coluna MOSTRA: em fatura paga o número é o `amountPaid` (o que entrou),
 * não o que ela pedia — ver `invoiceAmountOf`. Verde num número que fosse o valor cobrado diria
 * que entrou o que não entrou.
 */
const invoiceAmountTone = (row: any) => {
    if (row?.status === 'PAID') return 'text-green-600';
    if (row?.status === 'OPEN') return 'text-orange-500';
    return '';
};

/** Regresso: tri-state. NULL não é "não" — é operação SEM cessão de recebíveis. */
const recourseLabel = (value?: boolean | null) =>
    (value == null ? '-' : value ? 'Sim' : 'Não');



/**
 * Filtros vindos da URL. Servem a dois propósitos com um código só:
 *
 * 1. o item de menu já aponta para a tela filtrada (`?status=OPEN&collect=true`), do mesmo jeito
 *    que Operações de Crédito navega para Títulos com a operação já aplicada;
 * 2. deep-link de outra tela (uma operação, um charge, uma fatura específica).
 *
 * A leitura é dirigida pelo SCHEMA — nada de lista paralela de chaves aceitas, que sempre
 * desatualiza em relação aos filtros que a tela realmente tem.
 */
const filtersFromUrl = (params: URLSearchParams): AppliedFilter[] =>
    invoiceFilterSchema.flatMap((schema: FilterSchema) => {
        const raw = params.get(schema.key);
        if (raw == null || raw === '') return [];
        const base = { id: `url_${schema.key}`, key: schema.key, label: schema.label, type: schema.type };
        if (schema.type === 'boolean') {
            const operator: Operator = raw === 'false' ? 'is_false' : 'is_true';
            return [{ ...base, operator, value: null } as AppliedFilter];
        }
        // multivalorado: "OPEN,PAID" nos enums, "de,até" nas faixas. Sem o split, `between_dates`
        // receberia uma string e o serializador leria `value[0]` — a primeira LETRA da data.
        const multi = ['in', 'not_in', 'between', 'between_dates'].includes(schema.defaultOperator);
        return [{ ...base, operator: schema.defaultOperator, value: multi ? raw.split(',') : raw } as AppliedFilter];
    });

/**
 * Sem nada na URL, a tela abre no que interessa a quem cobra: o que está em aberto e sendo
 * cobrado. Passa pelo mesmo caminho da URL de propósito — o item de menu carrega exatamente esta
 * query string, e defini-la duas vezes é convite para as duas divergirem.
 */
export const DEFAULT_QUERY = 'status=OPEN&collect=true';

/**
 * A coluna "Em cobrança" quando o TÍTULO daquela fatura saiu das nossas mãos (AF-3674).
 *
 * ⚠️ Com flag, o "NÃO" SOME e fica só o ícone. Dizer "NÃO" ali seria verdade e mentira ao mesmo
 * tempo: é verdade que não estamos cobrando, mas o motivo não é desistência nossa — a dívida mudou
 * de dono, ou foi para a via judicial. O ícone carrega o porquê, que é o que falta na palavra.
 *
 * ⚠️ "Não deveriam acontecer" não é "não acontecem": as duas flags juntas, ou flag com cobrança
 * ativa, são estados que o banco permite (nada trava um com o outro — ver FlagTitleDebt). A tela
 * MOSTRA os dois ícones, e mostra o "SIM" se a cobrança seguir ativa, em vez de escolher uma
 * versão e esconder a outra de quem está investigando.
 */
export const collectCell = (row: any) => {
    const icons = [
        row.subrogation && {
            key: 'sub', node: <FaHandHoldingDollar />,
            title: 'Título sub-rogado: a cobrança passou para outro credor',
        },
        row.judicialPetition && {
            key: 'jud', node: <FaScaleBalanced />,
            title: 'Título enviado para o jurídico (petição judicial)',
        },
    ].filter(Boolean) as { key: string; node: JSX.Element; title: string }[];

    if (!icons.length) {
        return row.collect
            ? <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded font-medium">SIM</span>
            : <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded font-medium">NÃO</span>;
    }
    return (
        <span className="inline-flex items-center justify-center gap-1 text-slate-500">
            {icons.map(icon => (
                <span key={icon.key} title={icon.title} aria-label={icon.title}>{icon.node}</span>
            ))}
            {/* cobrança ativa num título flagado é contradição — e é exatamente por isso que
                aparece: esconder faria a tela concordar com um estado que ninguém deveria ter */}
            {row.collect && (
                <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded font-medium">SIM</span>
            )}
        </span>
    );
};

const InvoicesPage = () => {
    const [searchParams] = useSearchParams();
    const initialFilters = useMemo<AppliedFilter[]>(() => {
        const fromUrl = filtersFromUrl(searchParams);
        return fromUrl.length ? fromUrl : filtersFromUrl(new URLSearchParams(DEFAULT_QUERY));
        // searchParams é recriado a cada render; a chave estável é a query string
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.toString()]);

    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    // vazio de propósito: sem `sort` o backend aplica a ordem de leitura da tela — contrato →
    // aditamento → vencimento —, que não cabe num único campo de coluna. Mesmo arranjo da tela de
    // Títulos. Escolhendo uma coluna, o contrato passa a desempatar: as faturas de um contrato
    // continuam adjacentes em qualquer ordenação.
    const [currentSortField, setCurrentSortField] = useState('');
    const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    // semeado com o que veio da URL (ou o padrão): a primeira busca já sai filtrada
    const [activeFilters, setActiveFilters] = useState<AppliedFilter[]>(initialFilters);
    // gatilho de recarga: a consulta de situação do boleto muda a fatura por EVENTO (o biller
    // publica BOLETO_PAID/BOLETO_CANCELED depois de olhar o banco), então a lista precisa poder
    // ser relida. O `useFetch` só refaz a chamada quando a string da URL muda — daí o `_r`.
    const [refreshKey, setRefreshKey] = useState(0);
    const [detailsInvoice, setDetailsInvoice] = useState<any>(null);
    const [boletoInvoice, setBoletoInvoice] = useState<any>(null);
    const [eventsInvoice, setEventsInvoice] = useState<any>(null);
    const [commentInvoice, setCommentInvoice] = useState<any>(null);

    const { address: API_URL, key: API_KEY } = appConfig.apis.billing;

    const sortParam = !currentSortField ? ''
        : currentSortOrder === 'DESC' ? `-${currentSortField}` : currentSortField;
    const params = useMemo(() => {
        const p = new URLSearchParams({
            page: String(currentPage + 1),
            size: String(currentPageSize),
        });
        if (sortParam) p.set('sort', sortParam);
        const filterParams = serializeFilters(activeFilters);
        filterParams.forEach((value, key) => p.set(key, value));
        return p;
    }, [currentPage, currentPageSize, sortParam, activeFilters]);

    const url = `${API_URL}/invoices?${params.toString()}&_r=${refreshKey}`;

    const handleFilterSearch = useCallback((filters: AppliedFilter[]) => {
        setActiveFilters(filters);
        setCurrentPage(0);
    }, []);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API_KEY || '' }
    ], [API_KEY]);

    const tableHeaders = useMemo(() => [
        // o default do ATable já é text-center; declaramos só quem foge dele
        { id: 'charge_id', title: '#', canSort: false, cssClass: OCULTA },
        { id: 'invoice_id', title: 'Fatura', canSort: false, cssClass: OCULTA },
        /* ⚠️ Esta coluna exibia a OPERAÇÃO. Vinha de `title_debt.contract_number`, que guarda ora
           o contrato ora a operação — medido em hml, 11.699 títulos com um e 20.557 com o outro.
           Agora vem do `sequencial_number` da operação, a mesma fonte da tela de Títulos, e a
           operação tem coluna própria ao lado. */
        { id: 'contract_label', title: 'Cont.', canSort: true, field: 'sequencial_number' },
        {
            id: 'operation_number', title: 'Operação', canSort: true, field: 'operation_number',
            cssClass: NOWRAP
        },
        /* Cabeçalho centralizado (o default) com conteúdo à esquerda: nome e documento formam um
           bloco de texto, e centrá-lo deixa cada linha começando num ponto diferente.

           `width: 1%` é o truque de tabela para a coluna ENCOLHER até o conteúdo. A tabela do
           ATable é `min-w-full` + `table-auto`: numa tela larga sobra largura, e o `table-auto`
           reparte a sobra proporcionalmente à largura natural de cada coluna — quase toda ela caía
           aqui, que é a única coluna de texto livre. Medido: 599px de célula para um nome de 95px,
           com todo o vazio à direita porque o conteúdo é alinhado à esquerda (nas colunas
           centralizadas a mesma sobra se divide dos dois lados e não se nota).

           Com o 1%, a coluna fica em 212px (o teto de 180px do nome + padding) e a sobra vai para
           as centralizadas. O `truncate max-w-[180px]` da célula continua sendo quem põe o teto —
           sem ele, a coluna encolheria até o NOME MAIS LONGO da página e voltaria a mandar na
           largura da tabela. */
        /* Quem ORIGINOU a dívida, à frente de quem a deve — a mesma ordem da tela de Títulos e do
           modal de faturas do pagamento. O `width: 1%` vale pelo mesmo motivo explicado acima:
           são as duas únicas colunas de texto livre desta tabela. */
        {
            id: 'issuer', title: 'Emitente / Cedente', canSort: false, width: '1%',
            cssClass: NOWRAP
        },
        // "Pagador" confundia com o pagador do PAGAMENTO (outra tela, outra pessoa) e com o
        // emitente acima. Aqui é quem DEVE esta fatura.
        { id: 'payer', title: 'Devedor', canSort: false, width: '1%' },
        // Ordem padrão das telas de cobrança: Natureza, Título/NF, Nº. O documento identifica a
        // dívida e o número a posiciona dentro dela.
        { id: 'business_reference', title: 'Natureza', canSort: false },
        // Regresso entre Natureza e Título/NF, a mesma posição da tela de Títulos
        { id: 'with_recourse', title: 'Rgs.', canSort: false },
        { id: 'document_title', title: 'Título/NF', canSort: false },
        // Nº do TÍTULO (fraciona a operação), não o da fatura, que fraciona o charge e vive em
        // details.installmentNumber.
        { id: 'title_installment_number', title: 'Nº', canSort: false },
        // Vencimento antes do Valor: a lista é varrida por PRAZO (o que vence primeiro), e o
        // valor é a consequência que se lê depois.
        { id: 'due_date', title: 'Vencimento', canSort: true, field: 'due_date' },
        /* O nominal do TÍTULO, antes do valor da fatura: vai do todo para a parte. Ordena pelo
           LATERAL do título eleito — `invoice_order_by` resolve essa chave à parte, porque o
           `SORTABLE` só conhece colunas da própria fatura. */
        {
            id: 'title_amount', title: 'Valor Título', canSort: true, field: 'title_amount',
            cssClass: DIREITA
        },
        /* "Valor" não dizia de quê, com o nominal do título ao lado medindo outra coisa. */
        { id: 'amount', title: 'Valor Fatura', canSort: true, field: 'amount', cssClass: DIREITA },
        { id: 'status', title: 'Status', canSort: true, field: 'status' },
        { id: 'collect', title: 'Cobr.', canSort: false },
        // entre "Em cobrança" e "Ações": o comentário é contexto da linha, não uma ação dela
        { id: 'notes', title: 'Comentários', canSort: false },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    const pageSorted = (field: string, order: 'ASC' | 'DESC') => {
        setCurrentSortField(field);
        setCurrentSortOrder(order);
    };

    const renderRows = (rows: any[]) => {
        return rows.map((row: any) => (
            // sem onClick na LINHA: a linha inteira abrindo o modal disparava no meio de uma
            // seleção de texto e no clique em qualquer célula. Quem abre detalhes é o botão.
            <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                {/* ocultas (ver OCULTA nos cabeçalhos): o desenvolvedor ainda as encontra no DOM
                    para achar a linha no banco */}
                <td className={`${OCULTA} px-2 py-2 text-sm text-center text-gray-500 font-mono`} title={row.chargeId}>
                    {row.chargeId ? row.chargeId.slice(0, 8) : '-'}
                </td>
                <td className={`${OCULTA} px-2 py-2 text-sm text-center text-gray-500 font-mono`} title={row.invoiceId}>
                    {row.invoiceId ? row.invoiceId.slice(0, 8) : '-'}
                </td>
                <td className="px-2 py-2 text-sm text-center font-medium truncate" title={row.creditOperationId}>
                    {row.contractLabel || '-'}
                </td>
                <td className="px-2 py-2 text-sm text-center truncate max-w-[140px]" title={row.operationNumber}>
                    {row.operationNumber || '-'}
                </td>
                <td className="px-2 py-2 text-sm text-left max-w-[196px]">
                    <PartyCell name={row.issuer?.name} documentNumber={row.issuer?.documentNumber} />
                </td>
                <td className="px-2 py-2 text-sm text-left max-w-[196px]">
                    <PartyCell name={row.payer?.name} documentNumber={row.payer?.documentNumber} />
                </td>
                <td className="px-2 py-2 text-sm text-center">
                    {row.businessReference ? (
                        <BadgeStatus
                            text={BUSINESS_REFERENCE_LABELS[row.businessReference] || row.businessReference}
                            color={scenarioColors[row.businessReference] || 'gray'}
                        />
                    ) : '-'}
                </td>
                <td className="px-2 py-2 text-sm text-center">{recourseLabel(row.withRecourse)}</td>
                {/* vem em `details`, não no topo: é campo da PRÓPRIA fatura, e o payload separa
                    por origem — o que vem do título (Contrato, Natureza, Nº) fica no topo. Ler
                    `row.documentTitle` devolvia undefined e a coluna saía toda com "-". */}
                <td className="px-2 py-2 text-sm text-center">{row.details?.documentTitle || '-'}</td>
                <td className="px-2 py-2 text-sm text-center">{row.titleInstallmentNumber ?? '-'}</td>
                <td className={`px-2 py-2 text-sm text-center ${isOverdue(row) ? 'text-red-600 font-medium' : ''}`}>
                    {formatDateBR(row.dueDate)}
                </td>
                <td className="px-2 py-2 text-sm text-right">{nominalAmountOf(row)}</td>
                {/* Paga mostra o que foi RECEBIDO, não o que ela pedia: os dois divergem em
                    desconto, acréscimo e pagamento a maior — os casos que alguém vai investigar —, e
                    o valor cobrado ali faria a linha contradizer o extrato.

                    NEGRITO porque é o número da LINHA: a fatura é o que esta tela lista, e o
                    nominal ao lado é contexto do título. Com os dois no mesmo peso, a coluna a
                    mais roubava a leitura da que a tela existe para mostrar. */}
                <td className={`px-2 py-2 text-sm text-right font-semibold ${invoiceAmountTone(row)}`}>
                    {invoiceAmountOf(row)}
                </td>
                <td className="px-2 py-2 text-sm text-center min-w-[140px]">
                    <BadgeStatus text={row.statusFormatted || row.status} color={statusColors[row.status] || 'gray'} />
                </td>
                <td className="px-2 py-2 text-sm text-center">
                    {collectCell(row)}
                </td>
                {/* Um comentário aparece inteiro; vários viram ícone + o ÚLTIMO, com todos no
                    tooltip. O mesmo componente do modal de Eventos de Cobrança — duas
                    implementações da mesma célula divergiriam na primeira mudança. */}
                <td className="px-2 py-2 text-sm text-left max-w-[260px] truncate">
                    <CommentCell notes={row.details?.notesList || []} />
                </td>
                <td className="px-2 py-2 text-sm text-center whitespace-nowrap">
                    <button
                        className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 mr-1"
                        onClick={() => setDetailsInvoice(row)}
                        title="Detalhes"
                    ><FaInfoCircle /></button>
                    {/* Boleto SEMPRE visível, desabilitado sem boleto: escondê-lo fazia a coluna
                        mudar de largura entre linhas e obrigava a comparar duas linhas para
                        descobrir que a diferença era "não tem boleto", e não "a tela não carregou".

                        `<span title>` em volta porque um <button disabled> engole o tooltip nativo
                        em alguns navegadores — e é justamente no desabilitado que o motivo importa
                        (mesmo recurso da ChargesPage). */}
                    <span title={row.boleto ? 'Boleto (dados / download)' : 'Esta fatura não tem boleto emitido'}>
                        <button
                            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 mr-1 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed dark:disabled:bg-slate-700 dark:disabled:text-slate-500"
                            onClick={() => setBoletoInvoice(row)}
                            disabled={!row.boleto}
                            aria-label="Boleto"
                        ><FaBarcode /></button>
                    </span>
                    <button
                        className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 mr-1"
                        onClick={() => setEventsInvoice(row)}
                        title="Eventos de cobrança (protesto, Serasa, notificação)"
                        aria-label="Eventos de cobrança"
                    ><FaGavel /></button>
                    {/* Sem estado desabilitado: comentar vale para fatura em QUALQUER estado,
                        inclusive cancelada e paga — é assim que se explica depois o que aconteceu
                        com uma cobrança encerrada. */}
                    <button
                        className="px-2 py-1 text-xs bg-slate-100 text-blue-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
                        onClick={() => setCommentInvoice(row)}
                        title="Adicionar comentário"
                        aria-label="Adicionar comentário"
                    ><FaCommentDots /></button>
                </td>
            </tr>
        ));
    };

    return (
        <>
            <PageList
                pageTitle="Cobrança — Faturas"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Cobrança', url: '#' },
                    { label: 'Cobrança — Faturas', url: '/billing/invoices' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={currentSortField}
                tableOrderSort={currentSortOrder}
                onTableSort={pageSorted}
                renderRows={renderRows}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={(p: number) => setCurrentPage(p)}
                onItemsPerPageChange={(s: number) => { setCurrentPageSize(s); setCurrentPage(0); }}
                itemsLabel="faturas"
            >
                {{
                    filterSlot: (
                        <FilterBuilder
                            schema={invoiceFilterSchema}
                            onSearch={handleFilterSearch}
                            collapsible
                            defaultCollapsed
                            title="Filtros"
                            initialFilters={initialFilters}
                        />
                    ),
                    controlSlot: (<div></div>),
                }}
            </PageList>
            {detailsInvoice && (
                <InvoiceDetailsModal
                    invoice={detailsInvoice}
                    onClose={() => setDetailsInvoice(null)}
                />
            )}
            {boletoInvoice && (
                <BoletoModal invoice={boletoInvoice} onClose={() => setBoletoInvoice(null)}
                    onChanged={() => setRefreshKey((k: number) => k + 1)} />
            )}
            {eventsInvoice && (
                <InvoiceBillingEventsModal invoice={eventsInvoice}
                    onClose={() => setEventsInvoice(null)}
                    onChanged={() => setRefreshKey((k: number) => k + 1)} />
            )}
            {commentInvoice && (
                <AddInvoiceCommentModal invoice={commentInvoice}
                    onClose={() => setCommentInvoice(null)}
                    onAdded={() => setRefreshKey((k: number) => k + 1)} />
            )}
        </>
    );
};

export default InvoicesPage;
